import React, { useState, useEffect } from 'react';
import authService from '../../../services/authServices';
import expenditureService from '../../../services/expenditureService';
import vendorService from '../../../services/vendorService';
import animalService from '../../../services/animalService';
import expenseCategoryService from '../../../services/expenseCategoryService';
import expenseTypeService from '../../../services/expenseTypeService';

const PAGE_SIZE = 10;

const formatCurrency = (amount) => {
  const num = Math.round(parseFloat(amount || 0));
  return 'Rs ' + num.toLocaleString('en-US');
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Format a Date as YYYY-MM-DD using LOCAL components (avoids UTC month-shift)
const toLocalYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const _now = new Date();
const CURRENT_MONTH_START = toLocalYMD(new Date(_now.getFullYear(), _now.getMonth(), 1));
const CURRENT_MONTH_TODAY = toLocalYMD(_now);

const isThisMonth = (dateVal) => {
  if (!dateVal) return false;
  const d = dateVal.includes('T') ? dateVal.split('T')[0] : String(dateVal).substring(0, 10);
  return d >= CURRENT_MONTH_START && d <= CURRENT_MONTH_TODAY;
};

// A palette to color category cards consistently
const CARD_COLORS = ['#27ae60', '#2980b9', '#8e44ad', '#e67e22', '#16a085', '#c0392b', '#2c3e50', '#d35400'];

// Category pill colors — falls back to a neutral style for any custom category
const getCategoryStyle = (category) => {
  const map = {
    'Medicine/Veterinary':    { background: '#e3f2fd', color: '#1565c0' },
    'Feed':                   { background: '#e8f5e9', color: '#2e7d32' },
    'Maintenance & Utilities':{ background: '#fff3e0', color: '#e65100' },
    'Labor':                  { background: '#f3e5f5', color: '#6a1b9a' },
    'Other':                  { background: '#f5f5f5', color: '#616161' },
  };
  const s = map[category] || { background: '#eef2f7', color: '#34495e' };
  return { ...s, padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' };
};

// ── Pagination Control ────────────────────────────────────────────────────────
const Pagination = ({ currentPage, totalPages, onPageChange, totalItems, pageSize }) => {
  if (totalPages <= 1) return null;
  const start = (currentPage - 1) * pageSize + 1;
  const end   = Math.min(currentPage * pageSize, totalItems);
  return (
    <div style={paginationStyles.wrapper}>
      <span style={paginationStyles.info}>Showing {start}–{end} of {totalItems} records</span>
      <div style={paginationStyles.controls}>
        <button onClick={() => onPageChange(1)}               disabled={currentPage === 1}          style={{ ...paginationStyles.btn, ...(currentPage === 1          ? paginationStyles.btnDisabled : {}) }}>«</button>
        <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}          style={{ ...paginationStyles.btn, ...(currentPage === 1          ? paginationStyles.btnDisabled : {}) }}>‹ Prev</button>
        <span style={paginationStyles.pageIndicator}>Page {currentPage} of {totalPages}</span>
        <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} style={{ ...paginationStyles.btn, ...(currentPage === totalPages ? paginationStyles.btnDisabled : {}) }}>Next ›</button>
        <button onClick={() => onPageChange(totalPages)}      disabled={currentPage === totalPages} style={{ ...paginationStyles.btn, ...(currentPage === totalPages ? paginationStyles.btnDisabled : {}) }}>»</button>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const ExpenditureTab = () => {
  const [expenditures,       setExpenditures]       = useState([]);
  const [stats,              setStats]              = useState(null);
  const [animals,            setAnimals]            = useState([]);
  const [loading,            setLoading]            = useState(true);
  const [error,              setError]              = useState(null);
  const [showAddModal,       setShowAddModal]       = useState(false);
  const [showVendorsModal,   setShowVendorsModal]   = useState(false);
  const [currentUser,        setCurrentUser]        = useState(null);
  const [vendors,            setVendors]            = useState([]);
  const [selectedVendor,     setSelectedVendor]     = useState(null);
  const [vendorCreditExps,   setVendorCreditExps]   = useState([]);
  const [showPayModal,       setShowPayModal]       = useState(false);
  const [payingCreditExp,    setPayingCreditExp]    = useState(null);
  const [payAmount,          setPayAmount]          = useState('');
  const [payDate,            setPayDate]            = useState(new Date().toISOString().split('T')[0]);
  const [payNotes,           setPayNotes]           = useState('');
  const [showAddVendorModal, setShowAddVendorModal] = useState(false);
  const [vendorForm,         setVendorForm]         = useState({ name: '', phone: '', address: '' });

  // ── DB-driven categories & types ──
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [expenseTypes,      setExpenseTypes]      = useState([]);

  // Search, filter & pagination
  const [searchQuery,  setSearchQuery]  = useState('');
  const [filterCat,    setFilterCat]    = useState('All');
  const [filterPayment,setFilterPayment]= useState('All'); // All | cash | credit
  const [currentPage,  setCurrentPage]  = useState(1);

  const [formData, setFormData] = useState({
    expense_date: new Date().toISOString().split('T')[0],
    category: '', expense_type: '', animal_tag: '', animal_name: '',
    quantity: '', unit: '', price_per_unit: '', amount: '', vendor: '', payment_method: '',
    notes: '', payment_type: 'cash', vendor_id: '',
  });

  // ── Add-mode toggle + grid-mode state (grid is an additional mode) ──
  const [addMode,         setAddMode]         = useState('single'); // 'single' | 'grid'
  const [gridCategory,    setGridCategory]    = useState('');
  const [gridGroup,       setGridGroup]       = useState('Female'); // 'Female' | 'Male'
  const [gridFemaleType,  setGridFemaleType]  = useState('milk');   // 'milk' | 'nonmilk'
  const [gridVendor,      setGridVendor]      = useState('');
  const [gridDate,        setGridDate]        = useState(new Date().toISOString().split('T')[0]);
  const [gridQty,         setGridQty]         = useState({});        // key: `${typeName}||${tag}` -> qty string
  const [gridSaving,      setGridSaving]      = useState(false);

  useEffect(() => {
    const user = authService.getCurrentUser();
    setCurrentUser(user);
    fetchData(); fetchAnimals(); fetchVendors();
    fetchExpenseCategories(); fetchExpenseTypes();
  }, []);

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [searchQuery, filterCat, filterPayment]);

  const fetchExpenseCategories = async () => {
    try { const res = await expenseCategoryService.getAll(); setExpenseCategories(res.data || []); }
    catch (err) { console.error('Error fetching expense categories:', err); }
  };

  const fetchExpenseTypes = async () => {
    try { const res = await expenseTypeService.getAll(); setExpenseTypes(res.data || []); }
    catch (err) { console.error('Error fetching expense types:', err); }
  };

  const fetchVendors = async () => {
    try { const res = await vendorService.getAllVendors(); setVendors(res.data || []); }
    catch (err) { console.error('Error fetching vendors:', err); }
  };

  const openVendorDetails = async (vendor) => {
    setSelectedVendor(vendor);
    try { const res = await vendorService.getCreditExpendituresByVendor(vendor.id); setVendorCreditExps(res.data || []); }
    catch (err) { console.error('Error fetching vendor credit exps:', err); }
  };

  const handleAddVendor = async (e) => {
    e.preventDefault();
    try {
      await vendorService.createVendor(vendorForm);
      alert('✅ Vendor added!');
      setShowAddVendorModal(false); setVendorForm({ name: '', phone: '', address: '' }); fetchVendors();
    } catch { alert('❌ Failed to add vendor'); }
  };

  const handleDeleteVendor = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    try { await vendorService.deleteVendor(id); alert('✅ Vendor deleted!'); setSelectedVendor(null); fetchVendors(); }
    catch { alert('❌ Failed to delete vendor'); }
  };

  const handlePayVendor = async (e) => {
    e.preventDefault();
    try {
      await vendorService.createVendorPayment({ vendor_id: selectedVendor.id, credit_expenditure_id: payingCreditExp.id, amount_paid: parseFloat(payAmount), payment_date: payDate, notes: payNotes });
      alert('✅ Payment recorded!');
      setShowPayModal(false); setPayAmount(''); setPayNotes(''); setPayingCreditExp(null);
      fetchVendors(); openVendorDetails(selectedVendor);
    } catch { alert('❌ Failed to record payment'); }
  };

  const fetchAnimals = async () => {
    try { const res = await animalService.getAllAnimals(); setAnimals(res.data || []); }
    catch (err) { console.error('Error fetching animals:', err); }
  };

  const fetchData = async () => {
    try {
      setLoading(true); setError(null);
      const [expRes, statsRes] = await Promise.all([
        expenditureService.getAllExpenditures(),
        expenditureService.getStats(),
      ]);
      setExpenditures(expRes.data   || []);
      setStats(statsRes.data        || null);
      setCurrentPage(1);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching expenditures:', err);
      setError('Failed to load data. Please try again.'); setLoading(false);
    }
  };

  // Category change → reset type, price, unit
  const handleCategoryChange = (category) =>
    setFormData({ ...formData, category, expense_type: '', price_per_unit: '', unit: '', amount: '' });

  // Type change → auto-fill price + unit from the selected DB type, recompute amount
  const handleExpenseTypeChange = (typeName) => {
    const selected = expenseTypes.find(t => t.category === formData.category && t.name === typeName);
    const price = selected ? parseFloat(selected.default_price) || 0 : 0;
    const unit  = selected ? (selected.unit || '') : '';
    const qty   = parseFloat(formData.quantity) || 0;
    const amount = qty > 0 ? (qty * price) : (price || '');
    setFormData(prev => ({
      ...prev,
      expense_type: typeName,
      price_per_unit: price || '',
      unit,
      amount: amount === 0 ? '' : amount,
    }));
  };

  // Quantity change → recompute amount = quantity × price_per_unit
  const handleQuantityChange = (value) => {
    const qty = parseFloat(value) || 0;
    const price = parseFloat(formData.price_per_unit) || 0;
    const amount = (qty > 0 && price > 0) ? qty * price : formData.amount;
    setFormData(prev => ({ ...prev, quantity: value, amount: (qty > 0 && price > 0) ? amount : prev.amount }));
  };

  // Price change → recompute amount too (price stays editable)
  const handlePriceChange = (value) => {
    const price = parseFloat(value) || 0;
    const qty = parseFloat(formData.quantity) || 0;
    const amount = (qty > 0 && price > 0) ? qty * price : formData.amount;
    setFormData(prev => ({ ...prev, price_per_unit: value, amount: (qty > 0 && price > 0) ? amount : prev.amount }));
  };

  const handleAnimalTagChange = (tagNumber) => {
    const animal = animals.find(a => a.tag_number === tagNumber);
    setFormData(prev => ({ ...prev, animal_tag: tagNumber, animal_name: animal ? animal.name : '' }));
  };

  const handleAddExpenditure = async (e) => {
    e.preventDefault();
    try {
      await expenditureService.createExpenditure(formData);
      alert('✅ Expenditure recorded successfully!');
      setShowAddModal(false); resetForm(); fetchData();
    } catch (err) { alert('❌ ' + (err.response?.data?.message || 'Failed to record expenditure')); }
  };

  const handleDeleteExpenditure = async (id) => {
    if (!window.confirm('Are you sure you want to delete this expenditure?')) return;
    try { await expenditureService.deleteExpenditure(id); alert('✅ Expenditure deleted successfully!'); fetchData(); }
    catch (err) { alert('❌ ' + (err.response?.data?.message || 'Failed to delete expenditure')); }
  };

  const resetForm = () => setFormData({
    expense_date: new Date().toISOString().split('T')[0],
    category: '', expense_type: '', animal_tag: '', animal_name: '',
    quantity: '', unit: '', price_per_unit: '', amount: '', vendor: '', payment_method: '',
    notes: '', payment_type: 'cash', vendor_id: '',
  });

  // ── Grid mode helpers ──────────────────────────────────────────────────────
  const resetGrid = () => {
    setGridCategory(''); setGridGroup('Female'); setGridFemaleType('milk');
    setGridVendor(''); setGridDate(new Date().toISOString().split('T')[0]); setGridQty({});
  };

  const handleGridQtyChange = (typeName, tag, value) => {
    setGridQty(prev => ({ ...prev, [`${typeName}||${tag}`]: value }));
  };

  // Animals belonging to the selected group.
  // Groups: Female → Milking / Non-milking(dry only); Male → adult males only; Child → all calves (male + female)
  const gridGroupAnimals = animals.filter(a => {
    if (a.is_active === false) return false;
    if (gridGroup === 'Child') return a.milk_status === 'child';
    if (gridGroup === 'Male')  return a.gender === 'Male' && a.milk_status !== 'child';
    // Female (adults only — children live in the Child group)
    if (a.gender !== 'Female' || a.milk_status === 'child') return false;
    return gridFemaleType === 'milk' ? a.milk_status === 'milk' : a.milk_status === 'dry';
  });

  // Expense types (rows) for the chosen grid category
  const gridTypeRows = expenseTypes.filter(t => t.category === gridCategory);

  // Live grid total + filled count
  const gridStats = (() => {
    let total = 0, filled = 0;
    gridTypeRows.forEach(t => {
      const price = parseFloat(t.default_price) || 0;
      gridGroupAnimals.forEach(a => {
        const q = parseFloat(gridQty[`${t.name}||${a.tag_number}`]);
        if (q > 0) { filled += 1; total += q * price; }
      });
    });
    return { total, filled };
  })();

  const handleGridSave = async () => {
    if (!gridCategory) { alert('❌ Please select an expense category.'); return; }
    if (gridTypeRows.length === 0) { alert('❌ No expense types in this category. Add them in Settings first.'); return; }
    if (gridGroupAnimals.length === 0) { alert('❌ No animals in the selected group.'); return; }

    // Build the list of records from filled cells
    const records = [];
    gridTypeRows.forEach(t => {
      const price = parseFloat(t.default_price) || 0;
      const unit  = t.unit || '';
      gridGroupAnimals.forEach(a => {
        const raw = gridQty[`${t.name}||${a.tag_number}`];
        const qty = parseFloat(raw);
        if (qty > 0) {
          records.push({
            expense_date: gridDate,
            category: gridCategory,
            expense_type: t.name,
            animal_tag: a.tag_number,
            animal_name: a.name || '',
            quantity: qty,
            unit,
            price_per_unit: price,
            amount: qty * price,
            vendor: gridVendor || '',
            payment_method: 'Cash',
            notes: '',
            payment_type: 'cash',
            vendor_id: '',
          });
        }
      });
    });

    if (records.length === 0) { alert('⚠️ Enter a quantity for at least one animal.'); return; }

    setGridSaving(true);
    let ok = 0, failed = 0;
    for (const rec of records) {
      try { await expenditureService.createExpenditure(rec); ok += 1; }
      catch (err) { console.error('Grid save failed for', rec, err); failed += 1; }
    }
    setGridSaving(false);
    alert(failed === 0 ? `✅ ${ok} expense record(s) saved!` : `✅ ${ok} saved, ❌ ${failed} failed. Please re-check the failed ones.`);
    setShowAddModal(false); resetGrid(); fetchData();
  };

  // Types available for the currently selected category
  const typeOptionsForCategory = expenseTypes.filter(t => t.category === formData.category);

  const isAdmin = currentUser?.role === 'businessman';

  // Filter pill categories = "All" + the admin-created categories
  const filterCategories = ['All', ...expenseCategories.map(c => c.name)];

  // ── This month's expenditures (for the summary cards) ─────────────────────
  const thisMonthExpenditures = expenditures.filter(e => isThisMonth(e.expense_date || e.date));
  const thisMonthTotal = thisMonthExpenditures.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);

  // Per-category totals for THIS MONTH, computed from real data (dynamic — works
  // with any admin-created category, no hardcoded names)
  const categoryTotals = (() => {
    const map = {};
    thisMonthExpenditures.forEach(e => {
      const cat = e.category || 'Uncategorized';
      map[cat] = (map[cat] || 0) + parseFloat(e.amount || 0);
    });
    return Object.entries(map)
      .map(([name, total]) => ({ name, total }))
      .sort((a, b) => b.total - a.total);
  })();

  // ── Filtered & paginated data ─────────────────────────────────────────────
  const filteredExpenditures = expenditures.filter(exp => {
    const matchesSearch = !searchQuery ||
      (exp.category     || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (exp.expense_type || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (exp.vendor       || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (exp.vendor_name  || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (exp.animal_tag   || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (exp.notes        || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCat     = filterCat     === 'All' || exp.category     === filterCat;
    const matchesPayment = filterPayment === 'All' || exp.payment_type === filterPayment;
    return matchesSearch && matchesCat && matchesPayment;
  });

  const totalPages        = Math.ceil(filteredExpenditures.length / PAGE_SIZE);
  const paginatedExp      = filteredExpenditures.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const filteredTotal     = filteredExpenditures.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);

  if (loading) return (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
      <h2 style={{ color: '#666' }}>Loading expenditure data...</h2>
    </div>
  );
  if (error) return (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
      <h2 style={{ color: '#e74c3c', marginBottom: '10px' }}>Error</h2>
      <p style={{ color: '#666' }}>{error}</p>
      <button onClick={fetchData} style={styles.retryButton}>🔄 Retry</button>
    </div>
  );

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
        <div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#2c3e50', marginBottom: '8px' }}>Expenditure Management</h2>
          <p style={{ color: '#7f8c8d', fontSize: '14px' }}>Track all farm expenses and costs</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => setShowVendorsModal(true)} style={{ ...styles.addButton, background: '#8e44ad' }}>🏪 Vendors</button>
          <button onClick={() => setShowAddModal(true)}     style={styles.addButton}>➕ Add Expenditure</button>
        </div>
      </div>

      {/* ── Stats Cards (This Month Total + dynamic per-category) ── */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={{ ...styles.statValue, color: '#e74c3c' }}>{formatCurrency(thisMonthTotal)}</div>
          <div style={styles.statLabel}>This Month Total</div>
        </div>
        {categoryTotals.map((c, i) => (
          <div key={c.name} style={styles.statCard}>
            <div style={{ ...styles.statValue, color: CARD_COLORS[i % CARD_COLORS.length] }}>{formatCurrency(c.total)}</div>
            <div style={styles.statLabel}>{c.name}</div>
          </div>
        ))}
      </div>

      {/* ── Search & Filter Bar ── */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Search */}
        <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
          <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '15px', color: '#95a5a6' }}>🔍</span>
          <input
            type="text"
            placeholder="Search by category, item, vendor, animal, notes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ ...styles.searchInput, paddingLeft: '38px' }}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '15px', color: '#95a5a6' }}>✕</button>
          )}
        </div>

        {/* Payment type filter */}
        <div style={{ display: 'flex', gap: '6px' }}>
          {[
            { key: 'All',    label: 'All' },
            { key: 'cash',   label: '💵 Cash' },
            { key: 'credit', label: '🏪 Credit' },
          ].map(({ key, label }) => (
            <button key={key} onClick={() => setFilterPayment(key)} style={{
              padding: '8px 14px', border: '2px solid', fontSize: '13px', fontWeight: '600', borderRadius: '8px', cursor: 'pointer',
              borderColor: filterPayment === key ? '#e74c3c' : '#e0e0e0',
              background:  filterPayment === key ? '#e74c3c' : 'white',
              color:       filterPayment === key ? 'white'   : '#7f8c8d',
            }}>{label}</button>
          ))}
        </div>
      </div>

      {/* Category filter pills */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {filterCategories.map(cat => {
          const count = cat === 'All' ? expenditures.length : expenditures.filter(e => e.category === cat).length;
          return (
            <button key={cat} onClick={() => setFilterCat(cat)} style={{
              padding: '6px 14px', border: '2px solid', fontSize: '12px', fontWeight: '600', borderRadius: '20px', cursor: 'pointer',
              borderColor: filterCat === cat ? '#2c3e50' : '#e0e0e0',
              background:  filterCat === cat ? '#2c3e50' : 'white',
              color:       filterCat === cat ? 'white'   : '#7f8c8d',
            }}>
              {cat} ({count})
            </button>
          );
        })}
        {(searchQuery || filterCat !== 'All' || filterPayment !== 'All') && (
          <span style={{ fontSize: '13px', color: '#7f8c8d', alignSelf: 'center', marginLeft: '4px' }}>
            {filteredExpenditures.length} result{filteredExpenditures.length !== 1 ? 's' : ''} · {formatCurrency(filteredTotal)}
          </span>
        )}
      </div>

      {/* ── Expenditure Table ── */}
      <div style={styles.tableContainer}>
        {/* Total bar */}
        <div style={{ padding: '10px 16px', background: '#f8f9fa', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', fontWeight: '600' }}>
          <span style={{ color: '#7f8c8d' }}>{filteredExpenditures.length} records</span>
          <span style={{ color: '#e74c3c' }}>Total: {formatCurrency(filteredTotal)}</span>
        </div>

        <table style={styles.table}>
          <thead>
            <tr style={styles.headerRow}>
              <th style={styles.th}>#</th>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Category</th>
              <th style={styles.th}>Item/Type</th>
              <th style={styles.th}>Animal</th>
              <th style={styles.th}>Quantity</th>
              <th style={styles.th}>Amount</th>
              <th style={styles.th}>Vendor</th>
              <th style={styles.th}>Payment</th>
              <th style={styles.th}>Notes</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredExpenditures.length === 0 ? (
              <tr>
                <td colSpan="11" style={styles.noData}>
                  <div style={{ fontSize: '48px', marginBottom: '10px' }}>💰</div>
                  {searchQuery || filterCat !== 'All' || filterPayment !== 'All'
                    ? 'No records match your search'
                    : 'No expenditure records found'}
                </td>
              </tr>
            ) : (
              paginatedExp.map((exp, idx) => (
                <tr key={exp.id} style={styles.row}>
                  <td style={{ ...styles.td, color: '#95a5a6', fontSize: '13px', width: '40px' }}>
                    {(currentPage - 1) * PAGE_SIZE + idx + 1}
                  </td>
                  <td style={styles.td}>{formatDate(exp.expense_date)}</td>
                  <td style={styles.td}><span style={getCategoryStyle(exp.category)}>{exp.category}</span></td>
                  <td style={styles.td}>{exp.expense_type}</td>
                  <td style={styles.td}>{exp.animal_tag ? `${exp.animal_tag}${exp.animal_name ? ' - ' + exp.animal_name : ''}` : '-'}</td>
                  <td style={styles.td}>{exp.quantity ? `${exp.quantity} ${exp.unit || ''}`.trim() : '-'}</td>
                  <td style={styles.td}><strong style={{ color: '#e74c3c' }}>{formatCurrency(exp.amount)}</strong></td>
                  <td style={styles.td}>{exp.payment_type === 'credit' ? (exp.vendor_name || '-') : (exp.vendor || '-')}</td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {exp.payment_type !== 'credit' && <span style={styles.paymentBadge}>{exp.payment_method}</span>}
                      {exp.payment_type === 'credit' && <span style={{ background: '#fff3e0', color: '#e65100', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>🏪 Credit</span>}
                    </div>
                  </td>
                  <td style={styles.td}>{exp.notes || '-'}</td>
                  <td style={styles.td}>
                    <button onClick={() => handleDeleteExpenditure(exp.id)} style={styles.deleteButton}>🗑️ Delete</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalItems={filteredExpenditures.length}
          pageSize={PAGE_SIZE}
        />
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          MODALS
      ══════════════════════════════════════════════════════════════════════ */}

      {/* Add Expenditure Modal */}
      {showAddModal && (
        <div style={styles.modalOverlay} onClick={() => setShowAddModal(false)}>
          <div style={{ ...styles.modal, maxWidth: addMode === 'grid' ? '950px' : '600px' }} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Add Expenditure</h2>

            {/* Mode toggle */}
            <div style={{ display: 'flex', gap: 0, marginBottom: '20px', border: '2px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden', width: 'fit-content' }}>
              <button type="button" onClick={() => setAddMode('single')} style={{ padding: '8px 18px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '700', background: addMode === 'single' ? '#e74c3c' : 'white', color: addMode === 'single' ? 'white' : '#7f8c8d' }}>Single expense</button>
              <button type="button" onClick={() => setAddMode('grid')} style={{ padding: '8px 18px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '700', background: addMode === 'grid' ? '#e74c3c' : 'white', color: addMode === 'grid' ? 'white' : '#7f8c8d' }}>By animal (grid)</button>
            </div>

            {/* ── SINGLE EXPENSE MODE (existing form, unchanged) ── */}
            {addMode === 'single' && (
            <form onSubmit={handleAddExpenditure}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Date {!isAdmin && <span style={{ fontSize: '11px', color: '#7f8c8d' }}>(Today only)</span>}</label>
                <input type="date" value={formData.expense_date} onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })} style={{ ...styles.input, background: isAdmin ? 'white' : '#f5f5f5', cursor: isAdmin ? 'text' : 'not-allowed' }} disabled={!isAdmin} />
                {!isAdmin && <div style={{ fontSize: '11px', color: '#7f8c8d', marginTop: '4px' }}>Only admin can change date for past expenses</div>}
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Select Animal <span style={{ fontSize: '11px', color: '#7f8c8d' }}>(Optional)</span></label>
                <select value={formData.animal_tag} onChange={(e) => handleAnimalTagChange(e.target.value)} style={styles.input}>
                  <option value="">No specific animal</option>
                  {animals.map((animal) => (<option key={animal.id} value={animal.tag_number}>{animal.tag_number}{animal.name ? ` — ${animal.name}` : ''} ({animal.type} / {animal.breed})</option>))}
                </select>
              </div>
              {formData.animal_name && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Animal Name</label>
                  <input type="text" value={formData.animal_name} style={{ ...styles.input, background: '#e8f5e9', fontWeight: '600' }} disabled />
                </div>
              )}
              <div style={styles.formGroup}>
                <label style={styles.label}>Expense Category <span style={{ color: '#e74c3c' }}>*</span></label>
                <select value={formData.category} onChange={(e) => handleCategoryChange(e.target.value)} style={styles.input} required>
                  <option value="">Select Category</option>
                  {expenseCategories.map(cat => (<option key={cat.id} value={cat.name}>{cat.name}</option>))}
                </select>
                {expenseCategories.length === 0 && (
                  <div style={{ fontSize: '11px', color: '#e67e22', marginTop: '4px' }}>No categories yet. Admin can add them in Settings.</div>
                )}
              </div>
              {formData.category && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Expense Type/Item <span style={{ color: '#e74c3c' }}>*</span></label>
                  <select value={formData.expense_type} onChange={(e) => handleExpenseTypeChange(e.target.value)} style={styles.input} required>
                    <option value="">Select Type</option>
                    {typeOptionsForCategory.map(t => (
                      <option key={t.id} value={t.name}>
                        {t.name}{parseFloat(t.default_price) > 0 ? ` — Rs ${parseFloat(t.default_price).toLocaleString()}${t.unit ? '/' + t.unit : ''}` : ''}
                      </option>
                    ))}
                  </select>
                  {typeOptionsForCategory.length === 0 && (
                    <div style={{ fontSize: '11px', color: '#e67e22', marginTop: '4px' }}>No types in this category yet. Admin can add them in Settings.</div>
                  )}
                </div>
              )}
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Quantity</label>
                  <input type="number" step="0.01" value={formData.quantity} onChange={(e) => handleQuantityChange(e.target.value)} style={styles.input} placeholder="e.g., 50" />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Unit</label>
                  <input type="text" value={formData.unit} onChange={(e) => setFormData({ ...formData, unit: e.target.value })} style={styles.input} placeholder="e.g. bag" />
                </div>
              </div>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Price / Unit (PKR)</label>
                  <input type="number" step="0.01" value={formData.price_per_unit} onChange={(e) => handlePriceChange(e.target.value)} style={styles.input} placeholder="Auto-fills from type" />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Amount (PKR) <span style={{ color: '#e74c3c' }}>*</span></label>
                  <input type="number" step="1" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} style={{ ...styles.input, background: '#f0f9ff', fontWeight: '600' }} placeholder="Qty × Price" required />
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Payment Type <span style={{ color: '#e74c3c' }}>*</span></label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  {['cash', 'credit'].map(type => (
                    <button key={type} type="button" onClick={() => setFormData({ ...formData, payment_type: type, vendor_id: '', vendor: '', payment_method: type === 'credit' ? 'Credit' : '' })}
                      style={{ flex: 1, padding: '10px', border: `2px solid ${formData.payment_type === type ? '#e74c3c' : '#e0e0e0'}`, borderRadius: '8px', background: formData.payment_type === type ? '#fdecea' : 'white', fontWeight: '700', cursor: 'pointer', fontSize: '14px', color: formData.payment_type === type ? '#e74c3c' : '#666' }}>
                      {type === 'cash' ? '💵 Cash' : '🏪 Credit'}
                    </button>
                  ))}
                </div>
              </div>
              {formData.payment_type === 'credit' ? (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Select Vendor <span style={{ color: '#e74c3c' }}>*</span></label>
                  <select value={formData.vendor_id} onChange={(e) => setFormData({ ...formData, vendor_id: e.target.value })} style={styles.input} required>
                    <option value="">Select Vendor</option>
                    {vendors.map(v => (<option key={v.id} value={v.id}>{v.name} {v.outstanding_balance > 0 ? `(Outstanding: Rs ${Math.round(v.outstanding_balance).toLocaleString()})` : ''}</option>))}
                  </select>
                  <div style={{ fontSize: '12px', color: '#8e44ad', marginTop: '4px', cursor: 'pointer', fontWeight: '600' }} onClick={() => setShowAddVendorModal(true)}>➕ Add new vendor</div>
                </div>
              ) : (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Vendor / Paid To</label>
                  <input type="text" value={formData.vendor} onChange={(e) => setFormData({ ...formData, vendor: e.target.value })} style={styles.input} placeholder="Name of vendor or person" />
                </div>
              )}
              {formData.payment_type === 'cash' && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Payment Method <span style={{ color: '#e74c3c' }}>*</span></label>
                  <select value={formData.payment_method} onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })} style={styles.input} required>
                    <option value="">Select Payment Method</option>
                    <option value="Cash">Cash</option><option value="Bank Transfer">Bank Transfer</option>
                    <option value="Cheque">Cheque</option><option value="JazzCash">JazzCash</option>
                    <option value="EasyPaisa">EasyPaisa</option>
                  </select>
                </div>
              )}
              <div style={styles.formGroup}>
                <label style={styles.label}>Description / Notes</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }} rows="3" placeholder="Additional details..." />
              </div>
              <div style={styles.modalButtons}>
                <button type="button" onClick={() => { setShowAddModal(false); resetForm(); }} style={styles.cancelButton}>Cancel</button>
                <button type="submit" style={styles.submitButton}>Save Expenditure</button>
              </div>
            </form>
            )}

            {/* ── BY-ANIMAL GRID MODE ── */}
            {addMode === 'grid' && (
            <div>
              {/* Selectors */}
              <div style={{ display: 'grid', gridTemplateColumns: gridGroup === 'Female' ? '1fr 1fr 1fr' : '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Expense Category <span style={{ color: '#e74c3c' }}>*</span></label>
                  <select value={gridCategory} onChange={(e) => setGridCategory(e.target.value)} style={styles.input}>
                    <option value="">Select Category</option>
                    {expenseCategories.map(cat => (<option key={cat.id} value={cat.name}>{cat.name}</option>))}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Animal Group <span style={{ color: '#e74c3c' }}>*</span></label>
                  <select value={gridGroup} onChange={(e) => setGridGroup(e.target.value)} style={styles.input}>
                    <option value="Female">Female</option>
                    <option value="Male">Male</option>
                    <option value="Child">Child (all calves)</option>
                  </select>
                </div>
                {gridGroup === 'Female' && (
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Female Type <span style={{ color: '#e74c3c' }}>*</span></label>
                    <select value={gridFemaleType} onChange={(e) => setGridFemaleType(e.target.value)} style={styles.input}>
                      <option value="milk">Milking</option>
                      <option value="nonmilk">Non-milking (Dry)</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Shared row: date + vendor + payment note */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Date</label>
                  <input type="date" value={gridDate} onChange={(e) => setGridDate(e.target.value)} style={{ ...styles.input, background: isAdmin ? 'white' : '#f5f5f5' }} disabled={!isAdmin} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Vendor / Paid To</label>
                  <input type="text" value={gridVendor} onChange={(e) => setGridVendor(e.target.value)} style={styles.input} placeholder="Optional" />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Payment</label>
                  <input type="text" value="Cash" style={{ ...styles.input, background: '#f5f5f5' }} disabled />
                </div>
              </div>

              {/* Grid */}
              {!gridCategory ? (
                <div style={{ padding: '30px', textAlign: 'center', color: '#95a5a6', fontSize: '14px', background: '#f8f9fa', borderRadius: '8px' }}>
                  Select an expense category to load its types.
                </div>
              ) : gridTypeRows.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#e67e22', fontSize: '13px', background: '#fff8f0', borderRadius: '8px' }}>
                  No expense types in "{gridCategory}". Add them in the Settings tab first.
                </div>
              ) : gridGroupAnimals.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#e67e22', fontSize: '13px', background: '#fff8f0', borderRadius: '8px' }}>
                  No animals in this group.
                </div>
              ) : (
                <div style={{ overflowX: 'auto', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                  <table style={{ borderCollapse: 'collapse', fontSize: '13px', minWidth: '100%' }}>
                    <thead>
                      <tr style={{ background: '#f8f9fa' }}>
                        <th style={{ position: 'sticky', left: 0, background: '#f8f9fa', padding: '10px 12px', textAlign: 'left', fontWeight: '700', color: '#2c3e50', borderRight: '2px solid #e0e0e0', minWidth: '160px', whiteSpace: 'nowrap' }}>Type (price)</th>
                        {gridGroupAnimals.map(a => (
                          <th key={a.tag_number} style={{ padding: '8px 6px', textAlign: 'center', fontWeight: '700', color: '#2c3e50', minWidth: '70px', whiteSpace: 'nowrap' }}>{a.tag_number}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {gridTypeRows.map(t => {
                        const price = parseFloat(t.default_price) || 0;
                        return (
                          <tr key={t.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                            <td style={{ position: 'sticky', left: 0, background: 'white', padding: '8px 12px', borderRight: '2px solid #e0e0e0', whiteSpace: 'nowrap' }}>
                              <strong>{t.name}</strong>
                              <div style={{ fontSize: '11px', color: '#95a5a6' }}>Rs {price.toLocaleString()}{t.unit ? '/' + t.unit : ''}</div>
                            </td>
                            {gridGroupAnimals.map(a => (
                              <td key={a.tag_number} style={{ padding: '4px', textAlign: 'center' }}>
                                <input
                                  type="number" step="0.5" min="0"
                                  value={gridQty[`${t.name}||${a.tag_number}`] || ''}
                                  onChange={(e) => handleGridQtyChange(t.name, a.tag_number, e.target.value)}
                                  style={{ width: '58px', textAlign: 'center', padding: '6px 4px', border: '2px solid #e0e0e0', borderRadius: '6px', fontSize: '13px' }}
                                  placeholder="0"
                                />
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Summary + actions */}
              <div style={{ background: '#e8f5e9', borderRadius: '8px', padding: '10px 14px', margin: '14px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: '#555' }}>Cells filled: <strong>{gridStats.filled}</strong> → {gridStats.filled} record(s)</span>
                <strong style={{ fontSize: '15px', color: '#27ae60' }}>Total: {formatCurrency(gridStats.total)}</strong>
              </div>
              <div style={styles.modalButtons}>
                <button type="button" onClick={() => { setShowAddModal(false); resetGrid(); }} style={styles.cancelButton}>Cancel</button>
                <button type="button" onClick={handleGridSave} disabled={gridSaving} style={{ ...styles.submitButton, opacity: gridSaving ? 0.7 : 1 }}>
                  {gridSaving ? '⏳ Saving...' : '💾 Save All Entries'}
                </button>
              </div>
            </div>
            )}

          </div>
        </div>
      )}

      {/* Vendors Modal */}
      {showVendorsModal && (
        <div style={styles.modalOverlay} onClick={() => { setShowVendorsModal(false); setSelectedVendor(null); }}>
          <div style={{ ...styles.modal, maxWidth: '700px' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={styles.modalTitle}>🏪 Vendors</h2>
              <button onClick={() => setShowAddVendorModal(true)} style={{ padding: '8px 16px', background: '#8e44ad', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>➕ Add Vendor</button>
            </div>
            {!selectedVendor ? (
              <table style={styles.table}>
                <thead>
                  <tr style={styles.headerRow}>
                    {['Name', 'Phone', 'Address', 'Outstanding', 'Actions'].map(h => (<th key={h} style={styles.th}>{h}</th>))}
                  </tr>
                </thead>
                <tbody>
                  {vendors.length === 0 ? (
                    <tr><td colSpan="5" style={styles.noData}>No vendors added yet</td></tr>
                  ) : vendors.map(v => (
                    <tr key={v.id} style={styles.row}>
                      <td style={styles.td}><span onClick={() => openVendorDetails(v)} style={{ cursor: 'pointer', color: '#8e44ad', fontWeight: '700', textDecoration: 'underline' }}>{v.name}</span></td>
                      <td style={styles.td}>{v.phone || '-'}</td>
                      <td style={styles.td}>{v.address || '-'}</td>
                      <td style={styles.td}><strong style={{ color: parseFloat(v.outstanding_balance) > 0 ? '#e74c3c' : '#27ae60' }}>Rs {Math.round(parseFloat(v.outstanding_balance || 0)).toLocaleString()}</strong></td>
                      <td style={styles.td}><button onClick={() => handleDeleteVendor(v.id)} style={{ padding: '4px 10px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>🗑️</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div>
                <button onClick={() => setSelectedVendor(null)} style={{ marginBottom: '16px', padding: '6px 14px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>← Back</button>
                <h3 style={{ color: '#8e44ad', marginBottom: '4px' }}>{selectedVendor.name}</h3>
                <p style={{ color: '#666', fontSize: '13px', marginBottom: '16px' }}>Outstanding: <strong style={{ color: '#e74c3c' }}>Rs {Math.round(parseFloat(selectedVendor.outstanding_balance || 0)).toLocaleString()}</strong></p>
                {vendorCreditExps.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#95a5a6' }}>No credit expenditures found</div>
                ) : vendorCreditExps.map(ce => (
                  <div key={ce.id} style={{ background: ce.status === 'settled' ? '#f0fff4' : '#fdf3ff', border: `1px solid ${ce.status === 'settled' ? '#a5d6a7' : '#ce93d8'}`, borderRadius: '8px', padding: '12px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: '700', fontSize: '15px' }}>{ce.category} — {ce.expense_type}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>{formatDate(ce.expense_date)}</div>
                      <div style={{ fontSize: '13px', marginTop: '4px' }}>
                        Total: <strong>Rs {Math.round(parseFloat(ce.total_amount)).toLocaleString()}</strong> |
                        Paid: <strong style={{ color: '#27ae60' }}>Rs {Math.round(parseFloat(ce.paid_amount)).toLocaleString()}</strong> |
                        Remaining: <strong style={{ color: '#e74c3c' }}>Rs {Math.round(parseFloat(ce.remaining_amount)).toLocaleString()}</strong>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end' }}>
                      <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', background: ce.status === 'settled' ? '#d4edda' : ce.status === 'partial' ? '#fff3cd' : '#f8d7da', color: ce.status === 'settled' ? '#155724' : ce.status === 'partial' ? '#856404' : '#721c24' }}>{ce.status}</span>
                      {ce.status !== 'settled' && (
                        <button onClick={() => { setPayingCreditExp(ce); setShowPayModal(true); }} style={{ padding: '5px 12px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>💵 Pay</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button onClick={() => { setShowVendorsModal(false); setSelectedVendor(null); }} style={{ padding: '10px 20px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Pay Vendor Modal */}
      {showPayModal && payingCreditExp && (
        <div style={{ ...styles.modalOverlay, zIndex: 1100 }} onClick={() => setShowPayModal(false)}>
          <div style={{ ...styles.modal, maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>💵 Pay Vendor</h2>
            <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '12px', marginBottom: '20px', fontSize: '13px' }}>
              <div><strong>{payingCreditExp.category} — {payingCreditExp.expense_type}</strong></div>
              <div style={{ marginTop: '6px' }}>Remaining: <strong style={{ color: '#e74c3c' }}>Rs {Math.round(parseFloat(payingCreditExp.remaining_amount)).toLocaleString()}</strong></div>
            </div>
            <form onSubmit={handlePayVendor}>
              <div style={styles.formGroup}><label style={styles.label}>Amount (Rs) *</label><input type="number" step="1" min="1" max={payingCreditExp.remaining_amount} value={payAmount} onChange={e => setPayAmount(e.target.value)} style={styles.input} placeholder="Enter amount" required /></div>
              <div style={styles.formGroup}><label style={styles.label}>Payment Date</label><input type="date" value={payDate} onChange={e => setPayDate(e.target.value)} style={styles.input} /></div>
              <div style={styles.formGroup}><label style={styles.label}>Notes</label><input type="text" value={payNotes} onChange={e => setPayNotes(e.target.value)} style={styles.input} placeholder="Optional notes" /></div>
              <div style={styles.modalButtons}>
                <button type="button" onClick={() => setShowPayModal(false)} style={styles.cancelButton}>Cancel</button>
                <button type="submit" style={{ ...styles.submitButton, background: '#27ae60' }}>💾 Record Payment</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Vendor Modal */}
      {showAddVendorModal && (
        <div style={{ ...styles.modalOverlay, zIndex: 1100 }} onClick={() => setShowAddVendorModal(false)}>
          <div style={{ ...styles.modal, maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>➕ Add Vendor</h2>
            <form onSubmit={handleAddVendor}>
              <div style={styles.formGroup}><label style={styles.label}>Vendor Name *</label><input type="text" value={vendorForm.name} onChange={e => setVendorForm({ ...vendorForm, name: e.target.value })} style={styles.input} placeholder="Vendor name" required /></div>
              <div style={styles.formGroup}><label style={styles.label}>Phone</label><input type="text" value={vendorForm.phone} onChange={e => setVendorForm({ ...vendorForm, phone: e.target.value })} style={styles.input} placeholder="03XX-XXXXXXX" /></div>
              <div style={styles.formGroup}><label style={styles.label}>Address</label><input type="text" value={vendorForm.address} onChange={e => setVendorForm({ ...vendorForm, address: e.target.value })} style={styles.input} placeholder="Address" /></div>
              <div style={styles.modalButtons}>
                <button type="button" onClick={() => setShowAddVendorModal(false)} style={styles.cancelButton}>Cancel</button>
                <button type="submit" style={{ ...styles.submitButton, background: '#8e44ad' }}>Add Vendor</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Pagination Styles ─────────────────────────────────────────────────────────
const paginationStyles = {
  wrapper:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderTop: '1px solid #f0f0f0', background: '#fafafa', flexWrap: 'wrap', gap: '10px' },
  info:          { fontSize: '13px', color: '#7f8c8d' },
  controls:      { display: 'flex', alignItems: 'center', gap: '6px' },
  btn:           { padding: '6px 12px', border: '1px solid #ddd', borderRadius: '6px', background: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  btnDisabled:   { opacity: 0.4, cursor: 'not-allowed' },
  pageIndicator: { padding: '6px 14px', background: '#e74c3c', color: 'white', borderRadius: '6px', fontSize: '13px', fontWeight: '600' },
};

// ── Component Styles ──────────────────────────────────────────────────────────
const styles = {
  addButton:    { padding: '10px 20px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  statsGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '24px' },
  statCard:     { background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', textAlign: 'center' },
  statValue:    { fontSize: '22px', fontWeight: '700', marginBottom: '6px' },
  statLabel:    { fontSize: '12px', color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: '0.5px' },
  searchInput:  { width: '100%', padding: '10px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', outline: 'none' },
  tableContainer:{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'auto' },
  table:        { width: '100%', borderCollapse: 'collapse' },
  headerRow:    { background: '#f8f9fa' },
  th:           { padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#2c3e50', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap' },
  row:          { borderBottom: '1px solid #f0f0f0' },
  td:           { padding: '16px', fontSize: '14px', color: '#2c3e50' },
  noData:       { padding: '60px 20px', textAlign: 'center', color: '#95a5a6', fontSize: '16px' },
  paymentBadge: { background: '#d4edda', color: '#155724', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
  retryButton:  { marginTop: '20px', padding: '12px 24px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflow: 'auto', padding: '20px' },
  modal:        { background: 'white', padding: '30px', borderRadius: '12px', width: '90%', maxWidth: '600px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)', maxHeight: '90vh', overflow: 'auto' },
  modalTitle:   { fontSize: '22px', fontWeight: '700', marginBottom: '24px', color: '#2c3e50' },
  formRow:      { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  formGroup:    { display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' },
  label:        { fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  input:        { width: '100%', padding: '10px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' },
  modalButtons: { display: 'flex', gap: '12px', marginTop: '24px' },
  cancelButton: { flex: 1, padding: '12px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  submitButton: { flex: 1, padding: '12px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  deleteButton: { padding: '6px 12px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
};

export default ExpenditureTab;